import { useEffect, useState } from "react";
import { useMsal } from "@azure/msal-react";
import { SkyBody1, SkyButton } from "@skypointcloud/skypoint-ui";
import { authService as auth } from "../../utils/authService";
import SkypointLogoLoader from "../SkypointLogoLander/SkypointLogoLoader";
import {
  POLICY_MISMATCH_EVENT,
  markPolicyMismatch,
  readPolicyMismatch,
} from "../../utils/b2cPolicyRecovery";

/**
 * Login Handler Component
 * Automatically triggers login when user is not authenticated — EXCEPT while a B2C
 * policy-mismatch recovery is in progress (see utils/b2cPolicyRecovery.ts):
 *  - "retrying": useB2CPolicies already started the one automatic sign-in; don't start another.
 *  - "stopped":  that attempt came back mismatched. Auto-login would silently round-trip through
 *    the still-valid SSO session and loop, so show a dead end with ONE manual retry instead.
 *    (Never AccountErrorScreen: its sign-out calls logoutRedirect and ends the shared session.)
 */
export const LoginHandler = () => {
  const { instance, inProgress } = useMsal();
  const [recovery, setRecovery] = useState(() => readPolicyMismatch());

  // Follow the marker: redirect handling is async and may decide after this mounted.
  useEffect(() => {
    const sync = () => setRecovery(readPolicyMismatch());
    window.addEventListener(POLICY_MISMATCH_EVENT, sync);
    return () => window.removeEventListener(POLICY_MISMATCH_EVENT, sync);
  }, []);

  useEffect(() => {
    // Re-read too: the marker is written asynchronously by useB2CPolicies.
    if (recovery || readPolicyMismatch()) return;
    // Trigger login redirect if no interaction is in progress
    if (inProgress === "none") {
      // Preserve the intended deep link (e.g. /scheduled-tours?instanceid=... from an alert
      // email) across the MSAL B2C auth round-trip. auth.login() handles both the OAuth
      // `state` channel and the sessionStorage fallback.
      const currentPath = window.location.pathname + window.location.search;
      auth.login(currentPath).catch((error) => {
        console.error("Login initiation failed:", error);
      });
    }
  }, [instance, inProgress, recovery]);

  if (recovery === "stopped") {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-4 p-6 text-center">
        <SkyBody1>We couldn&apos;t sign you in automatically.</SkyBody1>
        <SkyButton
          appearance="primary"
          onClick={() => {
            // This click IS the next attempt: mark it in flight so a mismatched return
            // lands back here rather than triggering a further automatic sign-in.
            markPolicyMismatch("retrying");
            auth.login(window.location.pathname + window.location.search).catch((error) => {
              console.error("Login initiation failed:", error);
            });
          }}
        >
          Sign in again
        </SkyButton>
      </div>
    );
  }

  return <SkypointLogoLoader />;
};
